import useProcess from '@hooks/useProcess';
import { CircularProgress } from '@mui/joy';
import { observer } from 'mobx-react';
import { useLayoutEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { LoaderBox } from '@/components/ui/loader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ParamsList } from '@/routes/routes.types';

import { DatabasesContainer } from './database.style';

const Databases = () => {
  const navigate = useNavigate();
  const databaseId = useParams<string>()[ParamsList.DatabaseId];
  const process = useProcess();

  const isProcessReady = !!process && !process.isSmallProcess;
  const databases = isProcessReady
    ? process.databases.map(database => ({
        id: database.id,
        getName: database.getName
      }))
    : [];

  const firstDatabaseId = databases.length > 0 ? databases[0].id : undefined;
  const databaseExists =
    !!databaseId && databases.some(db => db.id === databaseId);

  useLayoutEffect(() => {
    if (!isProcessReady) {
      return;
    }
    if (databases.length === 0) {
      if (databaseId) {
        navigate('', { replace: true });
      }
      return;
    }
    if (!(databaseId && databaseExists)) {
      navigate(firstDatabaseId as string, { replace: true });
    }
  }, [
    isProcessReady,
    firstDatabaseId,
    databases.length,
    databaseId,
    databaseExists,
    navigate
  ]);

  if (!process || process.isSmallProcess) {
    return (
      <LoaderBox>
        <CircularProgress />
      </LoaderBox>
    );
  }

  // Avoid rendering empty tabs while redirect is pending
  if (databases.length > 0 && !databaseId) {
    return null;
  }

  const currentTab = databaseId ?? '';

  const onTabChange = (newTabId: string) => {
    navigate(`${newTabId}`);
  };

  return (
    <DatabasesContainer>
      <Tabs
        className="flex h-full flex-col pl-2"
        onValueChange={onTabChange}
        value={currentTab}
      >
        <div className="-mt-0.5 min-h-0 flex-1">
          <Outlet />
        </div>
        <TabsList className="mt-2 flex flex-wrap items-center justify-start gap-1.5 bg-transparent">
          {databases.map(databaseModel => (
            <TabsTrigger
              className="shadow-verail data-[state=active]:shadow-verail"
              key={databaseModel.id}
              value={databaseModel.id}
            >
              {databaseModel.getName}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </DatabasesContainer>
  );
};

export default observer(Databases);
